import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Link, useNavigate } from 'react-router-dom';
import { getColumnsData } from './columns';
import { Grid } from 'gridjs-react';
import ApiService from '@/services/api';
import COAModal from './COAModal';

const ChartOfAccount = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [cooperativeId, setCooperativeId] = useState(null);

  useEffect(() => {
    const profile = JSON.parse(localStorage.getItem('user_profile'));
    const coopId = profile?.cooperative.id || 0;
    setCooperativeId(coopId);
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const profile = JSON.parse(localStorage.getItem('user_profile'));
      const cooperativeId = profile?.cooperative.id || 0;
      const response = await ApiService.getCOA(cooperativeId)
      if (response.status === 200) {
        setReport(response.data);
      }
    } catch (error) {
      setReport([]);
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Something went wrong!',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditData(null);
    setIsModalOpen(true);
  };

  const handleEdit = async (accountData) => {
    try {
      // Check if accountData exists
      if (!accountData) {
        throw new Error('No account data received');
      }

      console.log('Account Data:', accountData);

      // Set editData directly from the row data since it already has all the fields
      setEditData(accountData);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Failed to load account details:', error);
      console.error('Account data received:', accountData);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.message || 'Failed to load account details'
      });
    }
  };

  const handleDelete = async (accountData) => {
    try {
      if (!accountData) {
        throw new Error('No account data received');
      }

      const accountId = accountData.id || accountData.account_id;
      const accountName = accountData.account_name;

      if (!accountId) {
        throw new Error('Account ID not found');
      }

      const result = await Swal.fire({
        title: 'Apakah Anda yakin?',
        text: `Hapus akun "${accountName}"? Data yang dihapus tidak dapat dikembalikan!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Ya, Hapus!',
        cancelButtonText: 'Batal',
      });

      if (result.isConfirmed) {
        try {
          await ApiService.deleteChartOfAccount(accountId);
          Swal.fire({
            icon: 'success',
            title: 'Berhasil!',
            text: 'Akun berhasil dihapus',
            timer: 2000,
            showConfirmButton: false,
          });
          loadData();
        } catch (error) {
          console.error(error);
          Swal.fire({
            icon: 'error',
            title: 'Gagal!',
            text: error?.message || 'Gagal menghapus akun',
          });
        }
      }
    } catch (error) {
      console.error('Delete error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.message || 'Terjadi kesalahan'
      });
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditData(null);
  };

  const handleModalSuccess = () => {
    Swal.fire({
      icon: 'success',
      title: 'Berhasil!',
      text: editData ? 'Akun berhasil diupdate' : 'Akun berhasil ditambahkan',
      timer: 2000,
      showConfirmButton: false,
    });
    loadData();
  };

  return (
    <>
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link
              to="/dashboard"
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"></path>
              </svg>
              Dashboard
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
              </svg>
              <button
                onClick={() => navigate('/admin/list-principal-savings')}
                className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
              >
                Chart Of Account (COA)
              </button>
            </div>
          </li>
        </ol>
      </nav>
    
      <div className="flex flex-auto flex-col">
        <div className="card">
          <div className="card-header">
            <div className="flex justify-between items-center">
              <h4 className="card-title">Chart Of Account (COA)</h4>
              <div className="flex gap-2">
                <button
                  className="btn btn-sm bg-success text-white"
                  onClick={handleCreate}
                >
                  <i className="mgc_add_line me-2"></i>Tambah COA
                </button>
                <button
                  className="btn btn-sm bg-secondary text-white"
                  onClick={loadData}
                  disabled={loading}
                >
                  <i className="mgc_refresh_1_line me-2"></i>Refresh
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="overflow-x-auto">
              <Grid
                data={report}
                columns={getColumnsData(handleEdit, handleDelete)}
                pagination={{
                  enabled: true,
                  limit: 100, // Show 100 items per page (lebih dari cukup untuk COA)
                }}
                search={true}
                sort={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <COAModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        editData={editData}
        cooperativeId={cooperativeId}
      />
    </>
  );
};

export default ChartOfAccount;
